import React from 'react';
import { styled } from '@mui/material/styles';
import { AlertMessage, SingleLineText, TextArea } from '@bloomays-lib/ui.shared';
import NoteSystem from '../molecules/NoteSystem';
import { Button } from '@bloomays-lib/ui.shared';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { Theme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export type NPSMissionProps = {
  noteBloomer?: string;
  commentBloomer?: string;
  selected?: number;
  setSelected: (e: number) => void;
  comment?: string;
  setComment: (value: React.SetStateAction<string | undefined>) => void;
  onClick: (args: { selected?: number; comment?: string }) => Promise<void>;
  texts: {
    title: string;
    subtitle: string;
    placeholder: string;
    button: string;
    details: string;
    nameLink: string;
    url: string;
    btnTitle: string;
    feedbackBloomerTitle?: string;
  };
  stateNPS: { loading: boolean };
  showBusinessReferal: boolean;
};

const NPSMission = ({
  selected,
  setSelected,
  comment,
  setComment,
  texts,
  onClick,
  stateNPS,
  noteBloomer,
  commentBloomer,
  showBusinessReferal,
}: NPSMissionProps) => {
  const { t } = useTranslation('NPSMission');

  return (
    <div>
      <SingleLineText
        sx={{
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'flex-start',
          paddingBottom: '8px',
        }}
        variant={'heading5'}
        text={texts.title}
      />
      <SingleLineText
        sx={{
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'flex-start',
          textAlign: 'left',
        }}
        variant={'subtitle2Medium'}
        text={texts.subtitle}
      />
      <AlertMessageContainer>
        <AlertMessage
          icon="info"
          title={t('warning')}
          description={
            <span
              dangerouslySetInnerHTML={{
                __html: noteBloomer ? t('infoNotSharedToBloomer') : t('infoSharedToClient'),
              }}
            />
          }
        />
      </AlertMessageContainer>

      <NoteSystem selected={selected} setSelected={setSelected} />
      <StyledTextArea
        handleChange={setComment}
        name={'NPSMission'}
        value={comment || ''}
        required={false}
        placeholder={texts.placeholder}
      />
      <SingleLineText
        sx={(theme: Theme) => ({
          textAlign: 'left',
          color: theme.palette.neutral[30],
        })}
        variant={'body1Regular'}
        text={t('rgpdMessage', { ns: 'NPSMission' })}
      />

      <ContainerBloomerSign>
        {selected && selected <= 6 && (
          <ContainerTextHelp>
            <SingleLineText
              sx={(theme: Theme) => ({
                paddingRight: '4px',
                color: theme.palette.neutral[30],
                display: 'inline',
                fontSize: '14px',
              })}
              text={texts.details}
            />
            <BloomHelpHref href={texts.url} text={texts.nameLink} />
          </ContainerTextHelp>
        )}
        {noteBloomer && (
          <NoteBloomerContainer>
            <NoteBloomerDescription>{texts.feedbackBloomerTitle}</NoteBloomerDescription>
            <StyledNPSBloomer>
              <SingleLineText
                sx={(theme: Theme) => {
                  return {
                    color: theme.palette.neutral.white,
                    backgroundColor: theme.palette.blue[60],
                    borderRadius: 50,
                    width: '45px',
                    height: '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: theme.palette.blue[20],
                      color: theme.palette.neutral[80],
                      cursor: 'pointer',
                    },
                  };
                }}
                text={noteBloomer}
                variant={'subtitle2Medium'}
              />
              <StyledCNPSBloomerComment>
                <SingleLineText text={commentBloomer ? commentBloomer : t('noComment')} />
              </StyledCNPSBloomerComment>
            </StyledNPSBloomer>
          </NoteBloomerContainer>
        )}

        {showBusinessReferal && (
          <BusinessReferalContainer>
            <BusinessReferalTexts>
              <SingleLineText variant={'heading7'} text={t('businessReferalTitle', { ns: 'NPSMission' })} />
              <BusinessReferalDescription
                dangerouslySetInnerHTML={{ __html: t('businessReferalDescription', { ns: 'NPSMission' }) }}
              />
            </BusinessReferalTexts>
            <Button
              color="secondary"
              sx={{
                width: 'fit-content',
                minWidth: '160px',
                textWrap: 'nowrap',
              }}
              startIcon={<ArrowForwardIcon />}
              disable={false}
              textButton={t('businessReferalButton', { ns: 'NPSMission' })}
              onClick={() => window.open('https://www.bloomays.com/business-referrals', '_blank')}
            />
          </BusinessReferalContainer>
        )}

        <Button
          title={texts.btnTitle}
          disable={(!selected && selected !== 0) || stateNPS.loading}
          textButton={stateNPS.loading ? 'Envoi en cours' : texts.button}
          onClick={async () => await onClick({ selected: selected, comment })}
        />
      </ContainerBloomerSign>
    </div>
  ) as React.ReactNode;
};

export default NPSMission;
export { NPSMission };

const NoteBloomerContainer = styled('div')(
  () => `
    display: flex;
    flex-direction: column;
  `,
);

const NoteBloomerDescription = styled('span')(
  () => `
  width: 100%;
  text-align: left;
  font-size: small;
  `,
);

const StyledCNPSBloomerComment = styled('div')(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px;
  background-color: ${theme.palette.neutral[10]};
  border-radius: 10px;
  width: 90%;
  margin-left: 12px;
  `,
);

const StyledNPSBloomer = styled('div')(
  () => `
    display: flex;
    flex-direction: row nowrap;
    align-items: center;
    margin-bottom: 20px;
  `,
);

const AlertMessageContainer = styled('div')(({ theme }) => ({
  margin: '12px 0',
  padding: '12px',
  backgroundColor: theme.palette.other.yellow,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.other.yellowMedium}`,
}));

const StyledTextArea = styled(TextArea)(() => ({
  marginTop: '24px',
  borderRadius: '5px',
  width: '95%',
  minHeight: '50px',
  padding: '16px 12px',
}));

const ContainerBloomerSign = styled('div')(() => ({
  textAlign: 'right',
  paddingTop: '24px',
}));

const ContainerTextHelp = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'flex-end',
  alignItems: 'baseline',
  paddingBottom: '20px',
}));

const BloomHelpHref = styled(HrefLink)(({ theme }) => ({
  color: theme.palette.blue[60],
  textDecoration: 'none',
  display: 'inline',
  fontSize: '14px',
}));

const BusinessReferalContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  textAlign: 'left',
  alignItems: 'center',
  gap: '12px',
  margin: '12px 0',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const BusinessReferalTexts = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const BusinessReferalDescription = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
  },
}));
